import projectRouter from '@/modules/Project/router';
import {
  type NavigationGuardWithThis,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router';
import { Feature, featureFlagManager } from './featureflag_manager';
import { authManager, guardForCallbackPage, type User } from './plugins/auth';
import { useUser } from './store';
import { RouteNames } from './types/routes';

declare module 'vue-router' {
  interface RouteMeta {
    // Optional feature flag to check if the route should be shown
    feature_flag?: Feature;
    // Optional flag to check if the route requires authentication
    requiresAuth?: boolean;
  }
}

export const HOME_ROUTE = { name: RouteNames.PROJECT_HOME };

export function isRouteValid(value: RouteNames): boolean {
  return Object.values(RouteNames).includes(value);
}

const verifyAuth = async (to: RouteLocationNormalized): Promise<boolean> => {
  const user = useUser();
  if (!user.user) {
    const isValid = await user.validate();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!user.user || (user.user as User).expired || !isValid) {
      await authManager.signinRedirect({ state: { to: to.fullPath } });
      return false;
    }
  }

  if (to.matched.some((record) => record.meta.requiresAuth) && !user.hasAccess) {
    return false;
  }

  return true;
};

/**
 * Guard that checks if the user is authenticated and has the required privileges to access the route.
 */
export const routerGuard: NavigationGuardWithThis<undefined> = async (to, from, next) => {
  /**
   * Check if the route has a feature flag and if it's not active, redirect to the not found page.
   * This mechanism allows us to hide routes that aren't ready for the current production environment.
   * Feature flags can be specified in the route's meta data.
   *
   * e.g.
   * {
   *   path: '/projects/:projectId/chat',
   *   name: RouteNames.PROJECT_CHAT,
   *   meta: { feature_flag: Feature.CHAT },
   * }
   */
  const routeWithFeatureFlag = to.matched.find((record) => record.meta.feature_flag);
  if (
    routeWithFeatureFlag?.meta.feature_flag &&
    !featureFlagManager.isActive(routeWithFeatureFlag.meta.feature_flag)
  ) {
    next({ name: RouteNames.NOTFOUND });
    return;
  }

  /** Return early for all routes that don't require authentication
   *  Since there is no auth we can also not request projects
   */
  if (!to.matched.some((record) => record.meta.requiresAuth)) {
    next();
    return;
  }

  const isAuthenticated = await verifyAuth(to);
  if (!isAuthenticated) {
    next({ name: RouteNames.UNAUTHORIZED });
    return;
  }

  next();
};

export const routes: RouteRecordRaw[] = [
  // @ts-expect-error - this is a valid route
  {
    path: '/callback',
    name: RouteNames.LOGIN_CALLBACK,
    beforeEnter: async (to, from, next) => {
      await guardForCallbackPage(authManager, (returnedUser) => {
        const fallback = { name: 'App' };
        if (!returnedUser) {
          next(fallback);
          return;
        }
        const state = returnedUser.state as { to?: string };
        next(state.to ?? fallback);
      });
    },
  },
  {
    name: RouteNames.APP,
    path: '',
    components: {
      layout: () => import('@/layouts/TopBarLayout.vue'),
    },
    redirect: HOME_ROUTE,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: RouteNames.HOME,
        redirect: HOME_ROUTE,
        children: [...projectRouter],
      },
      {
        name: RouteNames.NOTFOUND,
        path: '/:firstPathMatch?/:projectId?/:secondPathMatch(.*)*',
        components: {
          belowTopBar: () => import('@/views/NotFound.vue'),
        },
      },
    ],
  },
  {
    path: '/:firstPathMatch?/:projectId?/:secondPathMatch(.*)*',
    redirect: { name: RouteNames.UNAUTHORIZED },
    components: {
      layout: () => import('@/layouts/TopBarLayout.vue'),
    },
    children: [
      {
        name: RouteNames.UNAUTHORIZED,
        path: '',
        components: {
          belowTopBar: () => import('@/views/Unauthorized.vue'),
        },
      },
    ],
  },
];
